import { useDownloadMediaUriFilename } from "@/api/media";
import { useRFS } from "@/api/rfs";
import {
  SupportingDocumentData,
  useSupportingDocumentByRfsId,
} from "@/api/supporting-document";
import { MISSING_ID_IN_ROUTE, ROLE, STATUS } from "@/utils/constant";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ContainerPrimary } from "../ui";
import { DownloadButton } from "@/components/ui/button";
import { FieldFile } from "@/components/form/field-file";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import { useDocumentStatusUpdater } from "@/features/opm/utils/rfsStatusUpdater";
import {
  CODE_META,
  SupportingDocumentField,
} from "../utils/useCheckingProfileValidations";
import { DataInfoWithBadge } from "@/components/ui/data-info";
import DateValidationStatus from "./DateValidationStatus";
import ValidationIcon from "@/components/shared/ValidationIcon";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";

export function SupportingDocumentHandler({
  field,
}: {
  field: SupportingDocumentField;
}) {
  const { id } = useParams();
  const { roles } = useCurrentUser();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const meta = CODE_META[field.code as keyof typeof CODE_META];

  const form = useFormContext();
  const media = form.watch(field.code);

  const { data: rfs } = useRFS({ id });
  const { data: supportingDocument } = useSupportingDocumentByRfsId({
    rfsId: id,
  });
  const { mutate: downloadMedia, isPending: isDownloading } =
    useDownloadMediaUriFilename();
  const { updateDocumentStatus } = useDocumentStatusUpdater();

  const docName = supportingDocument?.[
    `${meta.doc}Name` as keyof SupportingDocumentData
  ] as string | undefined;
  const docPath = supportingDocument?.[
    `${meta.doc}Path` as keyof SupportingDocumentData
  ] as string | undefined;
  const isRevise = supportingDocument?.[
    `${meta.doc}IsRevise` as keyof SupportingDocumentData
  ] as boolean | undefined;

  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);
  const isDisabled =
    isViewOnly ||
    (rfs?.statusRfsSubmission === STATUS.RFS.PREPARATION_DATA
      ? Boolean(media?.length)
      : isRevise !== true);

  function handleDownload() {
    if (!media?.id && !docName) {
      throw new Error(`${field.label} is not found`);
    }

    if (media?.id) {
      downloadMedia({
        path: media.path,
        fileName: media.originalFileName,
      });
    } else if (docName) {
      downloadMedia({
        path: docPath as string,
        fileName: docName,
      });
    }
  }

  const error = form.formState.errors[field.code];
  const errorMessage =
    error &&
    getErrorMessage({ error, status: rfs?.status, label: field.label });

  return (
    <ContainerPrimary className="justify-end">
      <span className="flex-grow whitespace-nowrap">
        <div className="flex gap-2">
          <div className="flex flex-col">
            <span>
              {field.label}
              {field.isRequired && <RequiredAsterisk />}
            </span>
            <DataInfoWithBadge value={field.status ?? "Unvalidated"} />
          </div>
          <ValidationIcon value={isRevise} reverse />
        </div>
      </span>
      <DateValidationStatus validation={field} />
      {(media?.id || docName) && (
        <DownloadButton onClick={handleDownload} loading={isDownloading} />
      )}
      <FieldFile
        name={field.code}
        label={field.label}
        hideLabel
        uploadApiUrl="/api/crud/media/upload"
        media={{
          target: meta.target,
          entityId: id,
          className: "supporting-document",
          type: field.code,
          after: () => {
            updateDocumentStatus({
              rfsId: id,
              overallStatus: "Validated",
              documentType: meta.status,
              rfsData: rfs,
            });
          },
        }}
        defaultName={media?.originalFileName || docName}
        isDisabled={isDisabled}
      />
      {errorMessage && (
        <span className="w-full text-sm font-medium text-red-500 dark:text-red-900">
          {errorMessage}
        </span>
      )}
    </ContainerPrimary>
  );
}

function getErrorMessage({
  error,
  status,
  label,
}: {
  error: unknown;
  status?: string;
  label: string;
}) {
  console.error(error);
  if (status === STATUS.RFS.REJECTED_DOCUMENT_RFS) {
    return `${label} files have not been updated.`;
  }
  return (error as { id?: { message: string } })?.id?.message ?? "Error";
}
